import { calculateCptCodes } from '../../../documents/form/views/cpt/CptCodesView';
import { StatusListData } from '../../../documents/types';

export interface ApplicableCptCodes {
    '76014': number;
    '76015': number;
    '76016': number;
    '76018': number;
    '76019': number;
}

const EMPTY_APPLICABLE_CODES: ApplicableCptCodes = {
    '76014': 0,
    '76015': 0,
    '76016': 0,
    '76018': 0,
    '76019': 0,
};

const normalizeText = (value: unknown): string => {
    if (typeof value !== 'string') return '';
    return value.trim();
};

const hasAtLeastOneTrigger = (value: unknown): boolean => {
    if (!Array.isArray(value)) return false;

    return value.some((triggerRow: any) => {
        if (typeof triggerRow === 'string') {
            return normalizeText(triggerRow).length > 0;
        }

        if (triggerRow && typeof triggerRow === 'object') {
            return normalizeText(triggerRow.trigger).length > 0;
        }

        return false;
    });
};

const hasUsableDocumentData = (
    documentData?: Record<string, any> | null
): documentData is Record<string, any> => {
    return !!documentData && Object.keys(documentData).length > 0;
};

const countMatchingCodes = (
    codes: Array<{ code?: string }>,
    targetCode: '76014' | '76015'
) => codes.filter((entry) => entry.code === targetCode).length;

const getTotalLoggedMinutes = (allTasks: unknown): number => {
    if (!Array.isArray(allTasks)) return 0;

    return allTasks.reduce((sum: number, task: any) => {
        return sum + (Number(task?.minutes) || 0);
    }, 0);
};

const hasAtLeastOneDeviceRegion = (value: unknown): boolean => {
    if (!Array.isArray(value)) return false;

    return value.some((item: any) => {
        if (item && typeof item === 'object') {
            return normalizeText(item.deviceRegionName).length > 0;
        }
        return false;
    });
};

const hasAtLeastOneAction = (value: unknown): boolean => {
    if (!Array.isArray(value)) return false;

    return value.some((actionRow: any) => {
        if (actionRow && typeof actionRow === 'object') {
            return normalizeText(actionRow.actionType).length > 0;
        }
        return false;
    });
};

const hasAtLeastOnePreparationAction = (value: unknown): boolean => {
    if (!Array.isArray(value)) return false;

    return value.some((actionRow: any) => {
        if (actionRow && typeof actionRow === 'object') {
            return normalizeText(actionRow.prepAction).length > 0;
        }
        return false;
    });
};

const hasAtLeastOneImplant = (
    items: unknown,
    excludedIndices?: unknown
): boolean => {
    if (!Array.isArray(items)) return false;

    const excluded = Array.isArray(excludedIndices) ? excludedIndices : [];

    return items.some((item: any, index: number) => {
        if (excluded.includes(index)) return false;
        if (item && typeof item === 'object') {
            return normalizeText(item.implantName).length > 0;
        }
        return false;
    });
};

export const resolveApplicableCptCodes = ({
    initialEvaluationData,
    riskBenefitData,
    positioningImmobilizationData,
    devicePreparationData,
    savedApplicableCodes,
}: {
    initialEvaluationData?: Record<string, any> | null;
    riskBenefitData?: Record<string, any> | null;
    positioningImmobilizationData?: Record<string, any> | null;
    devicePreparationData?: Record<string, any> | null;
    savedApplicableCodes?: ApplicableCptCodes | null;
}): ApplicableCptCodes => {
    const hasInitialEvaluationData = hasUsableDocumentData(
        initialEvaluationData
    );
    const hasRiskBenefitData = hasUsableDocumentData(riskBenefitData);
    const hasPositioningImmobilizationData = hasUsableDocumentData(
        positioningImmobilizationData
    );
    const hasDevicePreparationData = hasUsableDocumentData(
        devicePreparationData
    );

    if (
        !hasInitialEvaluationData &&
        !hasRiskBenefitData &&
        !hasPositioningImmobilizationData &&
        !hasDevicePreparationData
    ) {
        return savedApplicableCodes
            ? {
                  ...EMPTY_APPLICABLE_CODES,
                  ...savedApplicableCodes,
              }
            : EMPTY_APPLICABLE_CODES;
    }

    const timeBasedCodes = calculateCptCodes(
        getTotalLoggedMinutes(initialEvaluationData?.allTasks)
    );
    const riskBenefitStatusList = build76016StatusList(riskBenefitData || {});
    const positioningStatusList = build76019StatusList(
        positioningImmobilizationData || {},
        { initialImplants: initialEvaluationData?.implants }
    );
    const devicePreparationStatusList = build76018StatusList(
        devicePreparationData || {}
    );

    return {
        '76014': countMatchingCodes(timeBasedCodes, '76014'),
        '76015': countMatchingCodes(timeBasedCodes, '76015'),
        '76016': riskBenefitStatusList.state === 'success' ? 1 : 0,
        '76018': devicePreparationStatusList.state === 'success' ? 1 : 0,
        '76019': positioningStatusList.state === 'success' ? 1 : 0,
    };
};

export const build76016StatusList = (
    documentData: Record<string, any>
): StatusListData => {
    const requirements = [
        {
            id: 'triggerRequirement',
            label: 'Select at least one trigger',
            met: hasAtLeastOneTrigger(documentData?.triggers),
        },
        {
            id: 'benefitRequirement',
            label: 'Provide benefit analysis',
            met: normalizeText(documentData?.benefit).length > 0,
        },
        {
            id: 'riskRequirement',
            label: 'Provide risk analysis',
            met: normalizeText(documentData?.risk).length > 0,
        },
        {
            id: 'conclusionRequirement',
            label: 'Provide conclusion',
            met: normalizeText(documentData?.conclusion).length > 0,
        },
    ];

    const missingItems = requirements
        .filter((requirement) => !requirement.met)
        .map((requirement) => ({
            id: requirement.id,
            label: requirement.label,
            state: 'warning' as const,
        }));

    const isEligible = missingItems.length === 0;

    return {
        title: isEligible
            ? 'CPT 76016 - Eligible'
            : 'CPT 76016 - Not Yet Eligible',
        state: isEligible ? 'success' : 'warning',
        icon: isEligible ? 'check-circle' : 'warning',
        items: missingItems,
    };
};

export const build76019StatusList = (
    documentData: Record<string, any>,
    importedContext?: { initialImplants?: unknown }
): StatusListData => {
    const hasDeviceRegion = hasAtLeastOneDeviceRegion(
        documentData?.deviceRegionItems
    );
    const hasImplant = hasAtLeastOneImplant(
        importedContext?.initialImplants,
        documentData?._excluded_initialImplants
    );

    const requirements = [
        {
            id: 'deviceRegionRequirement',
            label: 'Provide device / region or implant',
            met: hasDeviceRegion || hasImplant,
        },
        {
            id: 'actionRequirement',
            label: 'Add at least one positioning/immobilization action',
            met: hasAtLeastOneAction(documentData?.actionItems),
        },
    ];

    const missingItems = requirements
        .filter((requirement) => !requirement.met)
        .map((requirement) => ({
            id: requirement.id,
            label: requirement.label,
            state: 'warning' as const,
        }));

    const isEligible = missingItems.length === 0;

    return {
        title: isEligible
            ? 'CPT 76019 - Eligible'
            : 'CPT 76019 - Not Yet Eligible',
        state: isEligible ? 'success' : 'warning',
        icon: isEligible ? 'check-circle' : 'warning',
        items: missingItems,
    };
};

export const build76018StatusList = (
    documentData: Record<string, any>
): StatusListData => {
    const requirements = [
        {
            id: 'deviceTypeRequirement',
            label: 'Select a device type',
            met: normalizeText(documentData?.deviceType).length > 0,
        },
        {
            id: 'ifuUrlRequirement',
            label: 'Provide IFU/registry URL',
            met: normalizeText(documentData?.ifuUrl).length > 0,
        },
        {
            id: 'actionRequirement',
            label: 'Add at least one preparation action',
            met: hasAtLeastOnePreparationAction(
                documentData?.preparationActions
            ),
        },
        {
            id: 'postStateRequirement',
            label: 'Document post-preparation device state',
            met: normalizeText(documentData?.postState).length > 0,
        },
        {
            id: 'preScanSignatureRequirement',
            label: 'Sign the pre-scan attestation',
            met: documentData?.preScanSelfAttested === true,
        },
        {
            id: 'postScanSignatureRequirement',
            label: 'Sign the post-scan attestation',
            met: documentData?.postScanSelfAttested === true,
        },
    ];

    const missingItems = requirements
        .filter((requirement) => !requirement.met)
        .map((requirement) => ({
            id: requirement.id,
            label: requirement.label,
            state: 'warning' as const,
        }));

    const isEligible = missingItems.length === 0;

    return {
        title: isEligible
            ? 'CPT 76018 - Eligible'
            : 'CPT 76018 - Not Yet Eligible',
        state: isEligible ? 'success' : 'warning',
        icon: isEligible ? 'check-circle' : 'warning',
        items: missingItems,
    };
};
